/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as R from "fp-ts/Reader"
import * as E from "fp-ts/Either"
import { Either } from "fp-ts/Either"
import { sequenceS } from "fp-ts/Apply"
import { cacheValue } from "../app-core/_common/cache-value"
import { WebAppConfig } from "../app-core/_common/web-app-config"

const KnownStageKeys = { dev: null, "dev-rx01": null, "tst-rx": null, "stg-rx": null, qa: null, prod: null }
type KnownStage = keyof typeof KnownStageKeys
const isKnownStage = (stage: string): stage is KnownStage => Object.keys(KnownStageKeys).includes(stage)
const byStage = (config: Record<KnownStage, string>) => (stage: string) =>
    isKnownStage(stage) ? config[stage] : config["dev"]

export const ifProd =
    <T>(prodValue: T, otherwise: (stage: string) => T) =>
    (stage: string): T =>
        stage === "prod" ? prodValue : otherwise(stage)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const constant = (value: string) => (stage: string) => value
const serverEnvOr =
    (variableName: string, orFn: (stage: string) => string) =>
    (stage: string): Either<Error, string> =>
        F.pipe(
            O.fromNullable(process.env[variableName]),
            O.getOrElse(() => orFn(stage)),
            E.right,
        )
const mandatoryServerEnv =
    (variableName: string) =>
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (stage: string): Either<Error, string> =>
        E.fromNullable(new Error(`Missing env variable ${variableName}`))(process.env[variableName])
const optionalServerEnv =
    (variableName: string) =>
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (stage: string): Either<Error, string | undefined> =>
        E.right(process.env[variableName])
const optionalBuildTimeEnv =
    (value: string | undefined) =>
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (stage: string): Either<Error, string | undefined> =>
        E.right(value)

const configByStage = {
    HTTP_URL: serverEnvOr(
        "HTTP_URL",
        ifProd(
            "https://api-rest.booking.digital.silversea.com/",
            (stage) => `https://api-rest.${stage}-booking.digital.silversea.com/`,
        ),
    ),
    WS_URL: serverEnvOr(
        "WS_URL",
        ifProd(
            "wss://api-ws.booking.digital.silversea.com",
            (stage) => `wss://api-ws.${stage}-booking.digital.silversea.com`,
        ),
    ),
    CYBERSOURCE_URL: serverEnvOr(
        "CYBERSOURCE_URL",
        byStage({
            dev: "https://testsecureacceptance.cybersource.com/embedded/pay",
            "dev-rx01": "https://testsecureacceptance.cybersource.com/embedded/pay",
            "tst-rx": "https://testsecureacceptance.cybersource.com/embedded/pay",
            "stg-rx": "https://testsecureacceptance.cybersource.com/embedded/pay",
            qa: "https://secureacceptance.cybersource.com/embedded/pay",
            prod: "https://secureacceptance.cybersource.com/embedded/pay",
        }),
    ),
    DEMETRA_URL: serverEnvOr(
        "DEMETRA_URL",
        byStage({
            dev: "https://api-stg.silversea.demetra.one/ecommercebff/payment",
            "dev-rx01": "https://api-stg.silversea.demetra.one/ecommercebff/payment",
            "tst-rx": "https://api-stg.silversea.demetra.one/ecommercebff/payment",
            "stg-rx": "https://api-stg.silversea.demetra.one/ecommercebff/payment",
            qa: "https://api.silversea.demetra.one/ecommercebff/payment",
            prod: "https://api.silversea.demetra.one/ecommercebff/payment",
        }),
    ),
    DOT_COM_API_BASE_URL: serverEnvOr(
        "DOT_COM_API_BASE_URL",
        ifProd("https://api2.digital.silversea.com/", (stage) => `https://${stage}-api2.digital.silversea.com/`),
    ),
    SANITY_DATASET: serverEnvOr(
        "SANITY_DATASET",
        byStage({
            dev: "dev",
            "dev-rx01": "dev-rx01",
            "tst-rx": "tst-rx",
            "stg-rx": "stg-rx",
            qa: "production", // TODO: temporary to allow content team to preview dynamic FAQs (old value: "qa")
            prod: "production",
        }),
    ),
    SANITY_PROJECT_ID: serverEnvOr("SANITY_PROJECT_ID", constant("rd0y3pad")),
    SANITY_TOKEN: optionalServerEnv("SANITY_TOKEN"),
    ROLLBAR_CLIENT_TOKEN: serverEnvOr("ROLLBAR_CLIENT_TOKEN", constant("43b588b3f8a54952a245f8b8e07c72be")),
    ROLLBAR_ENV: serverEnvOr(
        "ROLLBAR_ENV",
        byStage({
            dev: "development",
            "dev-rx01": "dev-rx01",
            "tst-rx": "tst-rx",
            "stg-rx": "stg-rx",
            qa: "qa",
            prod: "production",
        }),
    ),
    NEXT_BUILD_ID: optionalBuildTimeEnv(process.env.NEXT_BUILD_ID),
    NODE_ENV: optionalServerEnv("NODE_ENV"),
    GTM_AUTH: serverEnvOr("GTM_AUTH", constant("P74T13jd8KiFfCiSe0_V-Q")),
    GTM_ID: serverEnvOr("GTM_ID", constant("GTM-5CN7PQ8")),
    GTM_PREVIEW: serverEnvOr(
        "GTM_PREVIEW",
        byStage({ dev: "env-577", "dev-rx01": "env-577", "tst-rx": "env-577", "stg-rx": "env-577", qa: "", prod: "" }),
    ),
    GTM_PREVIEW_ENABLED: serverEnvOr(
        "GTM_PREVIEW_ENABLED",
        byStage({ dev: "true", "dev-rx01": "true", "tst-rx": "true", "stg-rx": "true", qa: "false", prod: "false" }),
    ),
    TRACKING_ENABLED: serverEnvOr(
        "TRACKING_ENABLED",
        byStage({ dev: "false", "dev-rx01": "false", "tst-rx": "false", "stg-rx": "false", qa: "true", prod: "true" }),
    ),
    SSC_ANALYTICS_CORE_URL: serverEnvOr(
        "SSC_ANALYTICS_CORE_URL",
        constant("https://analytics.silversea.com/index.js?version=1"),
    ),
    SSC_ANALYTICS_CORE_QA_URL: serverEnvOr(
        "SSC_ANALYTICS_CORE_QA_URL",
        constant("https://qa--silversea-com-analytics-core.netlify.app/index.js?version=1"),
    ),
    STAGE: mandatoryServerEnv("STAGE"),
    LIVE_PERSON_ACCOUNT_ID: serverEnvOr(
        "LIVE_PERSON_ACCOUNT_ID",
        byStage({
            dev: "65985902",
            "dev-rx01": "65985902",
            "tst-rx": "65985902",
            "stg-rx": "65985902",
            qa: "65985902",
            prod: "51532229",
        }),
    ),
}

const sequenceSR = sequenceS(R.Apply)
const sequenceSE = sequenceS(E.Apply)
const validateConfigurationByStage = (stage: string): WebAppConfig =>
    F.pipe(
        stage,
        sequenceSR(configByStage),
        sequenceSE,
        E.map(
            ({
                HTTP_URL,
                WS_URL,
                CYBERSOURCE_URL,
                DEMETRA_URL,
                DOT_COM_API_BASE_URL,
                SANITY_DATASET,
                SANITY_PROJECT_ID,
                SANITY_TOKEN,
                ROLLBAR_CLIENT_TOKEN,
                ROLLBAR_ENV,
                NEXT_BUILD_ID,
                NODE_ENV,
                GTM_AUTH,
                GTM_ID,
                GTM_PREVIEW,
                GTM_PREVIEW_ENABLED,
                SSC_ANALYTICS_CORE_URL,
                SSC_ANALYTICS_CORE_QA_URL,
                TRACKING_ENABLED,
                LIVE_PERSON_ACCOUNT_ID,
                STAGE,
            }): WebAppConfig => ({
                serverUrl: HTTP_URL,
                wsUrl: WS_URL,
                cybersourceUrl: CYBERSOURCE_URL,
                demetraUrl: DEMETRA_URL,
                dotComApiBaseUrl: DOT_COM_API_BASE_URL,
                sanity: {
                    dataset: SANITY_DATASET,
                    projectId: SANITY_PROJECT_ID,
                    token: SANITY_TOKEN || null,
                },
                rollbar: {
                    accessToken: ROLLBAR_CLIENT_TOKEN,
                    environment: ROLLBAR_ENV,
                    code_version: NEXT_BUILD_ID || null,
                },
                gtm: {
                    id: GTM_ID,
                    auth: GTM_AUTH,
                    preview: GTM_PREVIEW_ENABLED === "true" ? GTM_PREVIEW : null,
                },
                analytics: {
                    coreUrl: SSC_ANALYTICS_CORE_URL,
                    coreQaUrl: SSC_ANALYTICS_CORE_QA_URL,
                    trackingEnabled: TRACKING_ENABLED === "true",
                },
                livePerson: {
                    accountId: LIVE_PERSON_ACCOUNT_ID,
                },
                NODE_ENV: NODE_ENV || null,
                stage: STAGE,
            }),
        ),
        E.getOrElse((e): WebAppConfig => {
            throw e
        }),
    )

const config = cacheValue(() => validateConfigurationByStage(process.env.STAGE!))

export default config
